/* import __COLOCATED_TEMPLATE__ from './side-by-side-review.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { DeduplicatedAsyncData } from 'embercom/resources/utils/async-data';
import type ContentSectionRelationship from 'embercom/models/content-service/content-section-relationship';
import type ContentSection from 'embercom/models/content-service/content-section';
import type SuggestionService from 'embercom/services/suggestion-service';
import type Store from '@ember-data/store';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type { EntityIdentifierTuple } from 'embercom/services/content-suggestions-service';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type IntlService from 'ember-intl/services/intl';
import {
  humanReadableObjectNames,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import { EntityType } from 'embercom/models/data/entity-types';
import type ContentStatistic from 'embercom/models/outbound/content-statistic';
import { get } from 'embercom/lib/ajax';
import { capitalize } from '@ember/string';

export interface Args {
  activeContent: ContentSectionRelationship;
}

interface Signature {
  Args: Args;
  Element: HTMLElement;
}

export default class SideBySideReview extends Component<Signature> {
  @service declare suggestionService: SuggestionService;
  @service declare store: Store;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;

  @tracked sectionsToDelete: ContentSection[] = [];
  @tracked isProcessingDeletions = false;
  @tracked showBothDeletedConfirmation = false;

  get reviewRequestCreatedAt() {
    if (this.args.activeContent.contentReviewRequest) {
      return this.args.activeContent.contentReviewRequest.get('createdAt');
    }
    return '';
  }

  get isSourceMarkedForDeletion() {
    return this.sectionsToDelete.includes(this.args.activeContent.sourceSection);
  }

  get isTargetMarkedForDeletion() {
    return this.sectionsToDelete.includes(this.args.activeContent.targetSection);
  }

  @action
  async confirmBothDeleted() {
    if (this.isProcessingDeletions) {
      return;
    }

    this.showBothDeletedConfirmation = false;

    await this.handleSuggestion('accept');
  }

  @action
  cancelBothDeletedConfirmation() {
    this.showBothDeletedConfirmation = false;
  }

  @action
  updateCardState(section: ContentSection, deleted: boolean) {
    if (deleted) {
      if (!this.sectionsToDelete.includes(section)) {
        this.sectionsToDelete = [...this.sectionsToDelete, section];
      }
    } else {
      this.sectionsToDelete = this.sectionsToDelete.filter(
        (deletedSection) => deletedSection !== section,
      );
    }

    if (this.isSourceMarkedForDeletion && this.isTargetMarkedForDeletion) {
      this.showBothDeletedConfirmation = true;
    } else {
      this.showBothDeletedConfirmation = false;
    }
  }

  get app() {
    return this.appService.app;
  }

  private async processMarkedDeletions(): Promise<void> {
    let successfullyDeleted: ContentSection[] = [];
    let failedDeletions: ContentSection[] = [];

    for (let deletedSection of this.sectionsToDelete) {
      let contentToDelete = deletedSection.relatedContent;
      if (contentToDelete) {
        try {
          await contentToDelete.destroyRecord();
          contentToDelete.unloadRecord();
          successfullyDeleted.push(deletedSection);
        } catch (error) {
          console.error('Failed to delete marked content:', error);
          failedDeletions.push(deletedSection);
        }
      }
    }

    this.sectionsToDelete = this.sectionsToDelete.filter(
      (section) => !successfullyDeleted.includes(section),
    );

    // Show success notification for successfully deleted content
    if (successfullyDeleted.length > 0) {
      let contentType: string;

      if (successfullyDeleted.length === 1) {
        // Use specific content type for single deletion
        let entityType = successfullyDeleted[0].entityType;
        let objectName = humanReadableObjectNames[entityType];
        if (!objectName) {
          contentType = this.intl.t('knowledge-hub.content-editor.side-by-side-review.content');
        } else {
          contentType = objectName;
        }
      } else {
        // Use generic "Content" for multiple deletions
        contentType = this.intl.t('knowledge-hub.content-editor.side-by-side-review.content');
      }

      let successMessage = this.intl.t(
        'knowledge-hub.content-editor.side-by-side-review.delete-content.success-message',
        {
          contentType: capitalize(contentType),
        },
      );

      this.notificationsService.notifyConfirmation(successMessage);
    }

    if (failedDeletions.length > 0) {
      throw new Error(
        `Failed to delete ${failedDeletions.length} of ${this.sectionsToDelete.length + successfullyDeleted.length} sections`,
      );
    }
  }

  get aiStatsBySection(): Map<string, ContentStatistic[]> {
    return this.aiStatsLoader.value ?? new Map();
  }

  get loadingAiStats(): boolean {
    return this.aiStatsLoader.isLoading;
  }

  get activeContent() {
    return this.args.activeContent;
  }

  @action
  getAiStatsForSection(section: ContentSection): ContentStatistic[] {
    if (!section || !section.entityType || !section.entityId) {
      return [];
    }
    let sectionKey = `${section.entityType}:${section.entityId}`;
    return this.aiStatsBySection.get(sectionKey) ?? [];
  }

  @action
  async handleSuggestion(action: 'accept' | 'reject') {
    this.trackAnalytics(action);

    let reviewRequest = this.store.peekRecord(
      'content-service/content-review-request',
      this.args.activeContent.contentReviewRequest.get('id'),
    );
    let nextContent = this.contentSuggestionsService.getNextSuggestionFromList({
      entityId: parseInt(this.args.activeContent.id, 10),
      entityType: this.args.activeContent.entityType,
    });
    let currentSuggestionId = this.args.activeContent.id;
    let currentEntityType = this.args.activeContent.entityType;

    if (nextContent) {
      this.openContentInReviewMode(nextContent);
    } else {
      // Close the drawer editor if no other suggestion exists. In focus mode this
      // will reset the drawer editor query params which will show the empty state instead
      await this.knowledgeHubDrawerEditorService.closeDrawer();
    }

    if (action === 'accept') {
      await taskFor(this.suggestionService.approveSuggestion).perform(
        reviewRequest,
        EntityType.ContentSectionRelationship,
      );

      // Delete the content that was marked for deletion
      this.isProcessingDeletions = true;
      try {
        await this.processMarkedDeletions();
        this.sectionsToDelete = [];
      } catch (error) {
        console.error('Failed to process marked deletions:', error);
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.content-editor.delete.error-message'),
        );
        return;
      } finally {
        this.isProcessingDeletions = false;
      }
    } else if (action === 'reject') {
      await taskFor(this.suggestionService.rejectSuggestion).perform(reviewRequest);
    }

    // Clear deleted sections after handling suggestion
    this.sectionsToDelete = [];

    this.contentSuggestionsService.markSuggestionAsCompleted({
      entityId: parseInt(currentSuggestionId, 10),
      entityType: currentEntityType,
    });

    setTimeout(() => {
      this.contentSuggestionsService.removeSuggestionFromList({
        entity_id: parseInt(currentSuggestionId, 10),
        entity_type: currentEntityType,
      });
    }, 1000);
  }

  private trackAnalytics(action: 'accept' | 'reject') {
    let analyticsObject =
      action === 'accept' ? 'content_review_accept_suggestion' : 'content_review_reject_suggestion';

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: analyticsObject,
      entity_id: this.args.activeContent.entityId,
      entity_type: this.args.activeContent.entityType,
    });
  }

  private aiStatsLoader = DeduplicatedAsyncData(
    this,
    () => [this.args.activeContent.id, this.args.activeContent.entityType],
    async (activeContentId) => {
      if (!activeContentId) {
        return new Map<string, ContentStatistic[]>();
      }

      return await this.fetchAiContentStats();
    },
  );

  private async fetchAiContentStats(): Promise<Map<string, ContentStatistic[]>> {
    try {
      let statsMap = new Map<string, ContentStatistic[]>();

      // Get stats for both source and target sections
      let promises = [
        this.fetchAiStatsForSection(this.activeContent.sourceSection),
        this.fetchAiStatsForSection(this.activeContent.targetSection),
      ];

      let [sourceStats, targetStats] = await Promise.all(promises);

      // Store stats by section key
      let sourceKey = `${this.activeContent.sourceSection.entityType}:${this.activeContent.sourceSection.entityId}`;
      let targetKey = `${this.activeContent.targetSection.entityType}:${this.activeContent.targetSection.entityId}`;

      statsMap.set(sourceKey, sourceStats);
      statsMap.set(targetKey, targetStats);

      return statsMap;
    } catch (error) {
      return new Map<string, ContentStatistic[]>();
    }
  }

  private async fetchAiStatsForSection(section: ContentSection): Promise<ContentStatistic[]> {
    try {
      let aiStats = await get('/ember/content_service/contents/get_ai_content_stats', {
        entity_id: section.entityId,
        entity_type: section.entityType,
        app_id: this.app.id,
        start_time: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      });

      let stats: ContentStatistic[] = [];
      aiStats.forEach((stat: ContentStatistic) => {
        this.store.push(this.store.normalize('outbound/content-statistic', stat));
        let contentStat = this.store.peekRecord('outbound/content-statistic', stat.id);
        if (contentStat) {
          stats.push(contentStat);
        }
      });

      return stats;
    } catch (error) {
      return [];
    }
  }

  private openContentInReviewMode(content: EntityIdentifierTuple) {
    this.knowledgeHubDrawerEditorService.openReviewDrawer({
      activeContentId: content.entityId,
      activeContentType: objectNames[content.entityType],
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SideBySideReview': typeof SideBySideReview;
    'knowledge-hub/content-editor/side-by-side-review': typeof SideBySideReview;
  }
}
